import React, { useState, useEffect, useCallback, useRef } from "react";
import { cfThumbnail, cfFullImage } from "../../../../utils/imageUtils";

/**
 * Full-screen image lightbox.
 *
 * Used for automation screenshots and the cloud data-table gallery.
 * Left/right always move within `screenshots`. When `rowNavigation` is
 * provided (data table), up/down asks the parent to switch to another
 * loaded row — the modal itself stays mounted so the swap stays snappy.
 *
 * @param {string[]} screenshots Image URLs for the current set
 * @param {string} title Header label
 * @param {Function} onClose
 * @param {number|null} initialIndex
 * @param {Object|null} [rowNavigation]
 * @param {(direction: number, currentIndex: number) => void} rowNavigation.onNavigate
 * @param {boolean} rowNavigation.canGoUp
 * @param {boolean} rowNavigation.canGoDown
 * @param {string} [rowNavigation.label]
 * @param {Object|null} [cover] Product-mode cover picker: shows a "Use as cover"
 *   button that hands the current image to `onSelect`; `url` marks the image
 *   that is the cover right now.
 * @param {string|null} cover.url
 * @param {(url: string, index: number) => void} cover.onSelect
 */
const ImageGalleryModal = ({ screenshots, title, onClose, initialIndex = null, rowNavigation = null, cover = null }) => {
  const [currentIndex, setCurrentIndex] = useState(
    initialIndex !== null ? initialIndex : screenshots.length - 1
  );
  // Only tracks whether the first image has loaded (for initial skeleton)
  const [ready, setReady] = useState(false);
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());

  const modalRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const rowNavRef = useRef(rowNavigation);
  rowNavRef.current = rowNavigation;
  const hasRowNavigation = Boolean(rowNavigation?.onNavigate);

  const imageCount = screenshots?.length || 0;

  const goToPrevious = useCallback(() => {
    if (imageCount <= 1) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageCount - 1));
  }, [imageCount]);

  const goToNext = useCallback(() => {
    if (imageCount <= 1) return;
    setCurrentIndex((prev) => (prev < imageCount - 1 ? prev + 1 : 0));
  }, [imageCount]);

  const goToPreviousRow = useCallback(() => {
    const nav = rowNavRef.current;
    if (!nav?.onNavigate) return;
    nav.onNavigate(-1, currentIndexRef.current);
  }, []);

  const goToNextRow = useCallback(() => {
    const nav = rowNavRef.current;
    if (!nav?.onNavigate) return;
    nav.onNavigate(1, currentIndexRef.current);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goToPrevious();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goToNext();
    } else if (e.key === "ArrowUp" && hasRowNavigation) {
      e.preventDefault();
      goToPreviousRow();
    } else if (e.key === "ArrowDown" && hasRowNavigation) {
      e.preventDefault();
      goToNextRow();
    } else if (e.key === "Escape") {
      onClose();
    }
  }, [onClose, goToPrevious, goToNext, goToPreviousRow, goToNextRow, hasRowNavigation]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (initialIndex !== null) {
      setCurrentIndex(Math.min(Math.max(initialIndex, 0), Math.max(imageCount - 1, 0)));
    }
  }, [initialIndex, screenshots, imageCount]);

  // New row = new thumbnail set; keep the main image mounted so the previous
  // photo stays visible until the next one paints.
  useEffect(() => {
    setLoadedThumbnails(new Set());
  }, [screenshots]);

  // Preload adjacent full-size images
  useEffect(() => {
    if (screenshots.length <= 1) return;
    [
      (currentIndex + 1) % screenshots.length,
      (currentIndex - 1 + screenshots.length) % screenshots.length,
    ].forEach((i) => { new Image().src = cfFullImage(screenshots[i]); });
  }, [currentIndex, screenshots]);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    const thumb = thumbnailRefs.current[currentIndex];
    if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentIndex]);

  // Lock body scroll & auto-focus
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!screenshots || screenshots.length === 0) return null;

  // Parent may swap in a shorter row before this local index is re-clamped.
  const displayIndex = Math.min(currentIndex, screenshots.length - 1);
  const currentUrl = screenshots[displayIndex];
  const canPickCover = typeof cover?.onSelect === "function";
  const isCurrentCover = canPickCover && cover.url === currentUrl;

  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={`Image gallery: ${title}`}
      className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[200] flex flex-col p-5 animate-fade-in outline-none"
      onClick={onClose}
    >
      {/* Header bar — always at top */}
      <div
        className="flex items-center justify-between mb-3 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="text-white text-sm font-semibold tracking-tight truncate">{title}</h3>
          {rowNavigation?.label && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white/[0.08] text-[11px] font-medium text-zinc-400 tabular-nums flex-shrink-0">
              {rowNavigation.label}
            </span>
          )}
          {(screenshots.length > 1 || rowNavigation) && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white/[0.08] text-[11px] font-medium text-zinc-400 tabular-nums flex-shrink-0">
              {displayIndex + 1} / {screenshots.length}
            </span>
          )}
          {rowNavigation && (
            <span className="hidden sm:inline text-[10px] text-zinc-500 tracking-wide flex-shrink-0">
              ↑↓ rows · ←→ images
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canPickCover && (
            <button
              onClick={() => {
                if (!isCurrentCover) cover.onSelect(currentUrl, displayIndex);
              }}
              disabled={isCurrentCover}
              aria-pressed={isCurrentCover}
              title={isCurrentCover ? "This image is the product's cover" : "Show this image for the product in product mode"}
              className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-medium transition-colors duration-150 ${
                isCurrentCover
                  ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30 cursor-default"
                  : "bg-white/10 hover:bg-white/20 text-white"
              }`}
            >
              {isCurrentCover && (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {isCurrentCover ? "Cover" : "Use as cover"}
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close gallery"
            className="flex-shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-150"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main image area — fills remaining space, stable layout */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Initial skeleton — only before first image loads */}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse text-zinc-700">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
              </svg>
            </div>
          </div>
        )}

        {/* Single img element — never remounted, src changes in-place.
            Browser keeps showing the old image until the new one loads,
            so there's no flash or layout jump during navigation. */}
        <img
          src={cfFullImage(screenshots[displayIndex])}
          alt={`Screenshot ${displayIndex + 1}`}
          className={`max-w-full max-h-full object-contain rounded-lg transition-opacity duration-200 ${
            ready ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => { if (!ready) setReady(true); }}
          draggable={false}
        />

        {/* Navigation arrows — left/right stay on this row; up/down change row */}
        {screenshots.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              aria-label="Previous image"
              title="Previous image (←)"
              className="absolute left-3 p-2.5 rounded-full bg-black/50 hover:bg-black/70 ring-1 ring-white/10 hover:ring-white/20 transition-all duration-150 hover:scale-110"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              aria-label="Next image"
              title="Next image (→)"
              className="absolute right-3 p-2.5 rounded-full bg-black/50 hover:bg-black/70 ring-1 ring-white/10 hover:ring-white/20 transition-all duration-150 hover:scale-110"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
        {rowNavigation && (
          <>
            <button
              onClick={goToPreviousRow}
              disabled={!rowNavigation.canGoUp}
              aria-label="Previous row"
              title="Previous row (↑)"
              className={`absolute top-3 left-1/2 -translate-x-1/2 p-2.5 rounded-full bg-black/50 ring-1 ring-white/10 transition-all duration-150 ${
                rowNavigation.canGoUp
                  ? "hover:bg-black/70 hover:ring-white/20 hover:scale-110"
                  : "opacity-30 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button
              onClick={goToNextRow}
              disabled={!rowNavigation.canGoDown}
              aria-label="Next row"
              title="Next row (↓)"
              className={`absolute bottom-3 left-1/2 -translate-x-1/2 p-2.5 rounded-full bg-black/50 ring-1 ring-white/10 transition-all duration-150 ${
                rowNavigation.canGoDown
                  ? "hover:bg-black/70 hover:ring-white/20 hover:scale-110"
                  : "opacity-30 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {screenshots.length > 1 && (
        <div
          className="mt-3 flex justify-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent pb-1 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {screenshots.map((screenshot, index) => (
            <button
              key={`${screenshot}-${index}`}
              ref={(el) => (thumbnailRefs.current[index] = el)}
              onClick={() => setCurrentIndex(index)}
              aria-current={index === displayIndex ? 'true' : undefined}
              className={`flex-shrink-0 rounded-lg overflow-hidden transition-all duration-150 ${
                index === displayIndex
                  ? 'ring-2 ring-white/70 scale-[1.05]'
                  : 'ring-1 ring-white/[0.08] opacity-50 hover:opacity-80 hover:ring-white/20'
              }`}
              style={{ width: '100px', height: '60px' }}
            >
              <div className="relative w-full h-full bg-zinc-900">
                {!loadedThumbnails.has(index) && (
                  <div className="absolute inset-0 animate-pulse bg-zinc-800" />
                )}
                <img
                  src={cfThumbnail(screenshot)}
                  alt={`Thumbnail ${index + 1}`}
                  className={`w-full h-full object-cover transition-opacity duration-150 ${
                    loadedThumbnails.has(index) ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setLoadedThumbnails((prev) => new Set([...prev, index]))}
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGalleryModal;
